import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AssessorAccess,
    AssessorAccessRequest,
    AssessorAccessResponse,
    AssessorRanksSearchResponse,
    AssessorVesselGroupsSearchResponse,
    UpsertAssessorAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreAssessor.api.ts';
import {
    createApiAssessorAccess,
    deleteApiAssessorAccess,
    getApiAssessorAccess,
    getApiAssessorRanks,
    getApiAssessorVesselGroups,
    updateApiAssessorAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreAssessor.api.ts';
import { extractError } from '@utils/slice.ts';

interface CrewAssessorError {
    message?: string;
    [key: string]: any;
}

interface CrewAssessorState {
    error: CrewAssessorError | null;
    crewAssessorsData: AssessorAccessResponse | null;
    crewAssessorVesselGroupsData: AssessorVesselGroupsSearchResponse | null;
    crewAssessorRanksData: AssessorRanksSearchResponse | null;
    crewAssesseeRanksData: AssessorRanksSearchResponse | null;

    isGetCrewAssessorsPending: boolean;
    isCreateCrewAssessorPending: boolean;
    isUpdateCrewAssessorPending: boolean;
    isDeleteCrewAssessorPending: boolean;
    isGetCrewAssessorVesselGroupsPending: boolean;
    isGetCrewAssessorRanksPending: boolean;
    isGetCrewAssesseeRanksPending: boolean;
}

const initialState: CrewAssessorState = {
    error: null,
    crewAssessorsData: null,
    crewAssessorVesselGroupsData: null,
    crewAssessorRanksData: null,
    crewAssesseeRanksData: null,

    isGetCrewAssessorsPending: false,
    isCreateCrewAssessorPending: false,
    isUpdateCrewAssessorPending: false,
    isDeleteCrewAssessorPending: false,
    isGetCrewAssessorVesselGroupsPending: false,
    isGetCrewAssessorRanksPending: false,
    isGetCrewAssesseeRanksPending: false,
};

export const getCrewAssessors = createAsyncThunk<
    AssessorAccessResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/getCrewAssessors', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createCrewAssessor = createAsyncThunk<
    AssessorAccess,
    { tenantAlias: string; payload: UpsertAssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/createCrewAssessor', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiAssessorAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCrewAssessor = createAsyncThunk<
    AssessorAccess,
    { tenantAlias: string; accessId: number; payload: UpsertAssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/updateCrewAssessor', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiAssessorAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteCrewAssessor = createAsyncThunk<
    any,
    { tenantAlias: string; accessId: number },
    { rejectValue: CrewAssessorError }
>('crewAssessor/deleteCrewAssessor', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiAssessorAccess(tenantAlias, accessId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewAssessorVesselGroups = createAsyncThunk<
    AssessorVesselGroupsSearchResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/getCrewAssessorVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewAssessorRanks = createAsyncThunk<
    AssessorRanksSearchResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/getCrewAssessorRanks', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorRanks(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCrewAssesseeRanks = createAsyncThunk<
    AssessorRanksSearchResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: CrewAssessorError }
>('crewAssessor/getCrewAssesseeRanks', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorRanks(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const crewAssessorSlice = createSlice({
    name: 'crewAssessor',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getCrewAssessors.pending, (state) => {
            state.isGetCrewAssessorsPending = true;
            state.error = null;
        });
        builder.addCase(getCrewAssessors.fulfilled, (state, action) => {
            state.isGetCrewAssessorsPending = false;
            state.crewAssessorsData = action.payload;
        });
        builder.addCase(getCrewAssessors.rejected, (state, action) => {
            state.isGetCrewAssessorsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createCrewAssessor.pending, (state) => {
            state.isCreateCrewAssessorPending = true;
            state.error = null;
        });
        builder.addCase(createCrewAssessor.fulfilled, (state) => {
            state.isCreateCrewAssessorPending = false;
        });
        builder.addCase(createCrewAssessor.rejected, (state, action) => {
            state.isCreateCrewAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateCrewAssessor.pending, (state) => {
            state.isUpdateCrewAssessorPending = true;
            state.error = null;
        });
        builder.addCase(updateCrewAssessor.fulfilled, (state) => {
            state.isUpdateCrewAssessorPending = false;
        });
        builder.addCase(updateCrewAssessor.rejected, (state, action) => {
            state.isUpdateCrewAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteCrewAssessor.pending, (state) => {
            state.isDeleteCrewAssessorPending = true;
            state.error = null;
        });
        builder.addCase(deleteCrewAssessor.fulfilled, (state) => {
            state.isDeleteCrewAssessorPending = false;
        });
        builder.addCase(deleteCrewAssessor.rejected, (state, action) => {
            state.isDeleteCrewAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCrewAssessorVesselGroups.pending, (state) => {
            state.isGetCrewAssessorVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getCrewAssessorVesselGroups.fulfilled, (state, action) => {
            state.isGetCrewAssessorVesselGroupsPending = false;
            state.crewAssessorVesselGroupsData = action.payload;
        });
        builder.addCase(getCrewAssessorVesselGroups.rejected, (state, action) => {
            state.isGetCrewAssessorVesselGroupsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCrewAssessorRanks.pending, (state) => {
            state.isGetCrewAssessorRanksPending = true;
            state.error = null;
        });
        builder.addCase(getCrewAssessorRanks.fulfilled, (state, action) => {
            state.isGetCrewAssessorRanksPending = false;
            state.crewAssessorRanksData = action.payload;
        });
        builder.addCase(getCrewAssessorRanks.rejected, (state, action) => {
            state.isGetCrewAssessorRanksPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getCrewAssesseeRanks.pending, (state) => {
            state.isGetCrewAssesseeRanksPending = true;
            state.error = null;
        });
        builder.addCase(getCrewAssesseeRanks.fulfilled, (state, action) => {
            state.isGetCrewAssesseeRanksPending = false;
            state.crewAssesseeRanksData = action.payload;
        });
        builder.addCase(getCrewAssesseeRanks.rejected, (state, action) => {
            state.isGetCrewAssesseeRanksPending = false;
            state.error = extractError(action);
        });
    },
});

export default crewAssessorSlice.reducer;
